// Client-side plumbing src/App.tsx uses to talk to this app's own Identity BFF
// (functions/auth/*.ts) and the shadow "issues" CRUD API
// (functions/api/shadow/issues, see functions/lib/shadowCrudHandlers.ts).
// Every function here is a thin fetch wrapper with no authority/decision
// logic of its own -- the server is the only place ACL/audit/cap decisions
// are made (see AGENTS.md).
//
// Test-mode note: import.meta.env.MODE is "test" under `vitest run` (vite's
// own default), never in a real dev/production build. The exported defaults
// below use that to avoid ever issuing a real network request during
// `npm run test` -- there is no server behind jsdom's default
// http://localhost/ origin, and a real attempt would be either a slow
// ECONNREFUSED per test render or (worse) environment-dependent behavior.
// A test that wants to exercise the sign-in/create-issue UI paths passes
// its own IdentityClient/LiveIssuesClient fake as an App prop instead (see
// src/App.test.tsx).

import type { ApprovalRow, DocumentVersionRow, RfiRow, ShadowIssueRow } from "./c1/shadowFoundation/types";

export interface IdentitySessionState {
  readonly status: "loading" | "signed-out" | "signed-in";
  readonly subject?: string;
}

export interface IdentityClient {
  fetchSession(): Promise<{ active: boolean; subject?: string }>;
  signOut(): Promise<void>;
}

// Generic over the row shape a `functions/api/shadow/{resource}` path segment returns --
// issues/rfis/approvals all share the same list()/create() wire contract (see
// functions/lib/shadowCrudHandlers.ts), only the row type differs. `LiveIssuesClient` below is
// kept as a named alias so existing imports/tests do not need to change.
export interface LiveResourceClient<Row> {
  list(): Promise<readonly Row[]>;
  create(body: Record<string, unknown>): Promise<{ ok: boolean; reason?: string }>;
}

export type LiveIssuesClient = LiveResourceClient<ShadowIssueRow>;

// document_versions does not fit LiveResourceClient's JSON-body create() contract: an upload
// carries a raw byte body (functions/api/shadow/document_versions/upload.ts's query-parameter +
// raw-bytes shape) alongside the D1 row fields, and a download returns bytes to save, not JSON --
// see that route's and functions/api/shadow/document_versions/download.ts's header comments for
// the wire shapes this client mirrors. list() still reuses the same
// `GET /api/shadow/{resource}` contract every other resource uses (functions/lib/shadowResources.ts:
// `genericCreateSupported: false` only turns off the generic *create* path for this resource, not
// the generic list route).
export interface LiveDocumentUploadParams {
  readonly documentId: string;
  readonly version: number;
  readonly title: string;
  readonly kind: string;
  readonly bytes: Uint8Array;
}

export interface LiveDocumentsClient {
  list(): Promise<readonly DocumentVersionRow[]>;
  upload(params: LiveDocumentUploadParams): Promise<{ ok: boolean; reason?: string; row?: DocumentVersionRow }>;
  download(
    documentId: string,
    version: number,
  ): Promise<{ ok: boolean; reason?: string; bytes?: Uint8Array; contentSha256?: string }>;
}

const IS_TEST_MODE = import.meta.env.MODE === "test";

async function fetchSessionOverNetwork(): Promise<{ active: boolean; subject?: string }> {
  const response = await fetch("/auth/session", { credentials: "same-origin" });
  const body = (await response.json()) as { active?: boolean; subject?: string };
  return { active: body.active === true, subject: body.subject };
}

// `/auth/logout` clears this app's own session cookie and returns
// `{ endSessionUrl }` (functions/auth/logout.ts). This function then
// navigates the browser there itself -- a real top-level navigation, not
// something the fetch() above could do on its own -- so Identity's own
// remembered-session state for this client_id is cleared too, not just
// CDE's. See functions/_identity.ts's endSessionUrl() doc comment for why
// this two-step shape exists (bim-hub-integration/answers/
// 2026-08-13-logout-end-session-wiring-go.md). A malformed/missing
// endSessionUrl in the response (should not happen against this app's own
// server, but a defensive parse regardless) leaves the caller's local
// session cleared without the Identity round trip rather than throwing --
// the caller already refreshes session state afterward either way.
async function signOutOverNetwork(): Promise<void> {
  const response = await fetch("/auth/logout", { method: "POST", credentials: "same-origin" });
  const body = (await response.json().catch(() => null)) as { endSessionUrl?: unknown } | null;
  if (typeof body?.endSessionUrl === "string" && body.endSessionUrl !== "") {
    window.location.href = body.endSessionUrl;
  }
}

export const defaultIdentityClient: IdentityClient = IS_TEST_MODE
  ? {
      async fetchSession() {
        return { active: false };
      },
      async signOut() {
        // No-op in test mode; see this file's header comment.
      },
    }
  : {
      fetchSession: fetchSessionOverNetwork,
      signOut: signOutOverNetwork,
    };

async function listLiveResourceOverNetwork<Row>(resource: string): Promise<readonly Row[]> {
  const response = await fetch(`/api/shadow/${resource}`, { credentials: "same-origin" });
  if (!response.ok) throw new Error(`Live ${resource} list failed (HTTP ${response.status}).`);
  const body = (await response.json()) as { outcome?: string; rows?: unknown };
  if (body.outcome !== "listed" || !Array.isArray(body.rows)) {
    throw new Error(`Live ${resource} list response was not in the expected shape.`);
  }
  return body.rows as readonly Row[];
}

async function createLiveResourceOverNetwork(
  resource: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; reason?: string }> {
  const response = await fetch(`/api/shadow/${resource}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => null)) as { outcome?: string; reason?: string } | null;
  if (response.status === 201 && payload?.outcome === "written") return { ok: true };
  return {
    ok: false,
    reason: payload?.reason ?? `Create failed (HTTP ${response.status}, outcome "${payload?.outcome ?? "unknown"}").`,
  };
}

// Same test-mode discipline as this file's header comment describes: under `npm run test`, list()
// returns empty and create() reports a stable failure reason instead of ever reaching `fetch`.
function createLiveResourceClient<Row>(resource: string): LiveResourceClient<Row> {
  return IS_TEST_MODE
    ? {
        async list() {
          return [];
        },
        async create() {
          return { ok: false, reason: `Live ${resource} creation is unavailable in this test build.` };
        },
      }
    : {
        list: () => listLiveResourceOverNetwork<Row>(resource),
        create: (body) => createLiveResourceOverNetwork(resource, body),
      };
}

export const defaultLiveIssuesClient: LiveIssuesClient = createLiveResourceClient<ShadowIssueRow>("issues");
export const defaultLiveRfisClient: LiveResourceClient<RfiRow> = createLiveResourceClient<RfiRow>("rfis");
export const defaultLiveApprovalsClient: LiveResourceClient<ApprovalRow> =
  createLiveResourceClient<ApprovalRow>("approvals");

async function uploadLiveDocumentOverNetwork(
  params: LiveDocumentUploadParams,
): Promise<{ ok: boolean; reason?: string; row?: DocumentVersionRow }> {
  const query = new URLSearchParams({
    documentId: params.documentId,
    version: String(params.version),
    title: params.title,
    kind: params.kind,
  });
  // Body is the raw file bytes, not JSON -- matches
  // functions/api/shadow/document_versions/upload.ts's `request.arrayBuffer()` parsing exactly.
  const response = await fetch(`/api/shadow/document_versions/upload?${query.toString()}`, {
    method: "POST",
    credentials: "same-origin",
    // Cast needed under TS 7's stricter Uint8Array<ArrayBufferLike> vs BodyInit typing -- these
    // bytes come from an ordinary File.arrayBuffer(), never a SharedArrayBuffer.
    body: params.bytes as BodyInit,
  });
  const payload = (await response.json().catch(() => null)) as { outcome?: string; reason?: string; row?: DocumentVersionRow } | null;
  if (response.status === 201 && payload?.outcome === "uploaded" && payload.row) return { ok: true, row: payload.row };
  return {
    ok: false,
    reason: payload?.reason ?? `Upload failed (HTTP ${response.status}, outcome "${payload?.outcome ?? "unknown"}").`,
  };
}

async function downloadLiveDocumentOverNetwork(
  documentId: string,
  version: number,
): Promise<{ ok: boolean; reason?: string; bytes?: Uint8Array; contentSha256?: string }> {
  const query = new URLSearchParams({ documentId, version: String(version) });
  const response = await fetch(`/api/shadow/document_versions/download?${query.toString()}`, { credentials: "same-origin" });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { outcome?: string; reason?: string } | null;
    return { ok: false, reason: payload?.reason ?? `Download failed (HTTP ${response.status}, outcome "${payload?.outcome ?? "unknown"}").` };
  }
  const bytes = new Uint8Array(await response.arrayBuffer());
  return { ok: true, bytes, contentSha256: response.headers.get("x-content-sha256") ?? undefined };
}

// Same test-mode discipline as createLiveResourceClient above: under `npm run test`, list()
// returns empty and upload()/download() report a stable failure reason instead of ever reaching
// `fetch`. A test that wants to exercise the upload/download UI paths passes its own
// LiveDocumentsClient fake as an App prop instead (see src/App.test.tsx).
export const defaultLiveDocumentsClient: LiveDocumentsClient = IS_TEST_MODE
  ? {
      async list() {
        return [];
      },
      async upload() {
        return { ok: false, reason: "Live document upload is unavailable in this test build." };
      },
      async download() {
        return { ok: false, reason: "Live document download is unavailable in this test build." };
      },
    }
  : {
      list: () => listLiveResourceOverNetwork<DocumentVersionRow>("document_versions"),
      upload: uploadLiveDocumentOverNetwork,
      download: downloadLiveDocumentOverNetwork,
    };
